import { useEffect, useState } from 'react'
import Alert from '@mui/joy/Alert'
import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import CircularProgress from '@mui/joy/CircularProgress'
import FormControl from '@mui/joy/FormControl'
import FormLabel from '@mui/joy/FormLabel'
import IconButton from '@mui/joy/IconButton'
import Input from '@mui/joy/Input'
import Option from '@mui/joy/Option'
import Select from '@mui/joy/Select'
import Snackbar from '@mui/joy/Snackbar'
import Stack from '@mui/joy/Stack'
import Table from '@mui/joy/Table'
import Tab from '@mui/joy/Tab'
import TabList from '@mui/joy/TabList'
import TabPanel from '@mui/joy/TabPanel'
import Tabs from '@mui/joy/Tabs'
import Typography from '@mui/joy/Typography'
import { instantPayFeeApi, InstantPayFeeConfig, InstantPayFeePayer, InstantPayFeeTierKind, RecentInstantPayPayout } from 'api/instantPayFee'

// A tier as edited in the form — amounts in DISPLAY units (dollars for min/max/flat, percent for
// percent). Converted to the API shape (integer cents / basis points) on save.
interface DraftTier {
    minDollars: string
    maxDollars: string // '' = open-ended (null maxCents)
    kind: InstantPayFeeTierKind
    value: string // percent (%) when kind='percent', dollars ($) when kind='flat'
}

interface SnackbarState {
    open: boolean
    message: string
    color: 'success' | 'danger'
}

const parseNum = (s: string): number => {
    const n = parseFloat(s)
    return Number.isFinite(n) ? n : NaN
}

const dollarsToCents = (s: string): number => Math.round(parseNum(s) * 100)

function configToDrafts(config: InstantPayFeeConfig | null): DraftTier[] {
    if (!config || config.tiers.length === 0) {
        // Sensible starter: one open-ended percent tier at 0.95% (today's baseline).
        return [{ minDollars: '0', maxDollars: '', kind: 'percent', value: '0.95' }]
    }
    return config.tiers.map((t) => ({
        minDollars: (t.minCents / 100).toString(),
        maxDollars: t.maxCents === null ? '' : (t.maxCents / 100).toString(),
        kind: t.kind,
        value: t.kind === 'percent' ? (t.value / 100).toString() : (t.value / 100).toString(),
    }))
}

/** Convert draft tiers to the API config, validating along the way (returns a typed error, never throws). */
function draftsToConfig(payer: InstantPayFeePayer, drafts: DraftTier[]): { ok: true; config: InstantPayFeeConfig } | { ok: false; message: string } {
    if (drafts.length === 0) return { ok: false, message: 'Add at least one tier' }
    const tiers: InstantPayFeeConfig['tiers'] = []
    for (let i = 0; i < drafts.length; i++) {
        const d = drafts[i]
        const minCents = dollarsToCents(d.minDollars)
        if (!Number.isInteger(minCents) || minCents < 0) return { ok: false, message: `Tier ${i + 1}: min must be a non-negative dollar amount` }
        const maxCents = d.maxDollars.trim() === '' ? null : dollarsToCents(d.maxDollars)
        if (maxCents !== null && (!Number.isInteger(maxCents) || maxCents <= minCents))
            return { ok: false, message: `Tier ${i + 1}: max must be blank or greater than min` }
        // percent → basis points (0.95% -> 95 bps); flat dollars → cents.
        const value = d.kind === 'percent' ? Math.round(parseNum(d.value) * 100) : dollarsToCents(d.value)
        if (!Number.isInteger(value) || value < 0) return { ok: false, message: `Tier ${i + 1}: fee value must be a non-negative number` }
        tiers.push({ minCents, maxCents, kind: d.kind, value })
    }
    return { ok: true, config: { payer, tiers } }
}

const fmtMoney = (cents: number): string => `$${(cents / 100).toFixed(2)}`

export default function InstantPayFeePage() {
    const [payer, setPayer] = useState<InstantPayFeePayer>(InstantPayFeePayer.CARRIER)
    const [drafts, setDrafts] = useState<DraftTier[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [payouts, setPayouts] = useState<RecentInstantPayPayout[]>([])
    const [payoutsLoading, setPayoutsLoading] = useState(false)
    const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', color: 'success' })

    useEffect(() => {
        let alive = true
        instantPayFeeApi
            .getConfig()
            .then((config) => {
                if (!alive) return
                setPayer(config?.payer ?? InstantPayFeePayer.CARRIER)
                setDrafts(configToDrafts(config))
            })
            .catch(() => alive && setSnackbar({ open: true, message: 'Failed to load fee config', color: 'danger' }))
            .finally(() => alive && setLoading(false))
        return () => {
            alive = false
        }
    }, [])

    const loadPayouts = () => {
        setPayoutsLoading(true)
        instantPayFeeApi
            .getRecentPayouts(100)
            .then(setPayouts)
            .catch(() => setSnackbar({ open: true, message: 'Failed to load recent payouts', color: 'danger' }))
            .finally(() => setPayoutsLoading(false))
    }

    const updateTier = (index: number, patch: Partial<DraftTier>) => {
        setDrafts((prev) => prev.map((t, i) => (i === index ? { ...t, ...patch } : t)))
    }
    const addTier = () => setDrafts((prev) => [...prev, { minDollars: '', maxDollars: '', kind: 'percent', value: '' }])
    const removeTier = (index: number) => setDrafts((prev) => prev.filter((_, i) => i !== index))

    const save = async () => {
        const built = draftsToConfig(payer, drafts)
        if (!built.ok) {
            setSnackbar({ open: true, message: built.message, color: 'danger' })
            return
        }
        setSaving(true)
        try {
            const saved = await instantPayFeeApi.setConfig(built.config)
            setPayer(saved.payer)
            setDrafts(configToDrafts(saved))
            setSnackbar({ open: true, message: 'Fee schedule saved', color: 'success' })
        } catch (e) {
            const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Save failed'
            setSnackbar({ open: true, message: msg, color: 'danger' })
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1100 }}>
            <Typography level="h3" sx={{ mb: 0.5 }}>
                Instant Pay
            </Typography>
            <Typography level="body-sm" sx={{ mb: 2, color: 'text.secondary' }}>
                Platform-wide payout fee schedule + recent successful payouts across all carriers.
            </Typography>

            <Tabs defaultValue={0} onChange={(_, v) => v === 1 && payouts.length === 0 && loadPayouts()}>
                <TabList>
                    <Tab>Fee Schedule</Tab>
                    <Tab>Recent Payouts</Tab>
                </TabList>

                <TabPanel value={0}>
                    <Card variant="outlined" sx={{ gap: 2 }}>
                        <FormControl sx={{ maxWidth: 320 }}>
                            <FormLabel>Who pays the fee</FormLabel>
                            <Select value={payer} onChange={(_, v) => v && setPayer(v)}>
                                <Option value={InstantPayFeePayer.CARRIER}>Carrier (added on top; driver receives full amount)</Option>
                                <Option value={InstantPayFeePayer.DRIVER}>Driver (deducted from payout; driver receives amount − fee)</Option>
                            </Select>
                        </FormControl>

                        <Typography level="title-sm">Tiers (by payout amount)</Typography>
                        <Alert color="neutral" variant="soft" size="sm">
                            A payout falls in the tier whose range [min, max) contains it. Leave a tier&apos;s max blank for &quot;and above&quot;. Percent
                            tiers are a % of the payout; flat tiers are a fixed dollar fee. Amounts are in dollars.
                        </Alert>

                        <Table size="sm" sx={{ '& th': { fontWeight: 600 } }}>
                            <thead>
                                <tr>
                                    <th style={{ width: '20%' }}>Min ($)</th>
                                    <th style={{ width: '20%' }}>Max ($, blank = ∞)</th>
                                    <th style={{ width: '20%' }}>Type</th>
                                    <th style={{ width: '25%' }}>Fee (% or $)</th>
                                    <th style={{ width: '15%' }} />
                                </tr>
                            </thead>
                            <tbody>
                                {drafts.map((t, i) => (
                                    <tr key={i}>
                                        <td>
                                            <Input
                                                size="sm"
                                                type="number"
                                                value={t.minDollars}
                                                onChange={(e) => updateTier(i, { minDollars: e.target.value })}
                                                placeholder="0"
                                            />
                                        </td>
                                        <td>
                                            <Input
                                                size="sm"
                                                type="number"
                                                value={t.maxDollars}
                                                onChange={(e) => updateTier(i, { maxDollars: e.target.value })}
                                                placeholder="∞"
                                            />
                                        </td>
                                        <td>
                                            <Select size="sm" value={t.kind} onChange={(_, v) => v && updateTier(i, { kind: v })}>
                                                <Option value="percent">Percent (%)</Option>
                                                <Option value="flat">Flat ($)</Option>
                                            </Select>
                                        </td>
                                        <td>
                                            <Input
                                                size="sm"
                                                type="number"
                                                value={t.value}
                                                onChange={(e) => updateTier(i, { value: e.target.value })}
                                                startDecorator={t.kind === 'flat' ? '$' : undefined}
                                                endDecorator={t.kind === 'percent' ? '%' : undefined}
                                            />
                                        </td>
                                        <td>
                                            <IconButton size="sm" color="danger" variant="soft" onClick={() => removeTier(i)} disabled={drafts.length <= 1}>
                                                ✕
                                            </IconButton>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>

                        <Stack direction="row" spacing={1}>
                            <Button variant="outlined" size="sm" onClick={addTier}>
                                + Add tier
                            </Button>
                            <Box sx={{ flex: 1 }} />
                            <Button loading={saving} onClick={save}>
                                Save schedule
                            </Button>
                        </Stack>
                    </Card>
                </TabPanel>

                <TabPanel value={1}>
                    <Card variant="outlined">
                        <Stack direction="row" sx={{ mb: 1 }}>
                            <Typography level="title-sm">Recent successful payouts</Typography>
                            <Box sx={{ flex: 1 }} />
                            <Button variant="outlined" size="sm" onClick={loadPayouts} loading={payoutsLoading}>
                                Refresh
                            </Button>
                        </Stack>
                        {payoutsLoading && payouts.length === 0 ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress size="sm" />
                            </Box>
                        ) : payouts.length === 0 ? (
                            <Typography level="body-sm" sx={{ color: 'text.secondary', p: 2 }}>
                                No successful payouts yet.
                            </Typography>
                        ) : (
                            <Box sx={{ overflowX: 'auto' }}>
                                <Table size="sm" stickyHeader>
                                    <thead>
                                        <tr>
                                            <th>Driver</th>
                                            <th>Carrier</th>
                                            <th>Amount</th>
                                            <th>Fee</th>
                                            <th>Net to driver</th>
                                            <th>Payer</th>
                                            <th>Transfer</th>
                                            <th>When</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payouts.map((p) => (
                                            <tr key={p.id}>
                                                <td>{p.driverName}</td>
                                                <td>{p.projectName}</td>
                                                <td>{fmtMoney(p.amountCents)}</td>
                                                <td>{fmtMoney(p.feeCents)}</td>
                                                <td>{fmtMoney(p.netCents)}</td>
                                                <td>{p.payer ?? '—'}</td>
                                                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.moovTransferId?.slice(0, 12) ?? '—'}</td>
                                                <td>{new Date(p.completedAt).toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Box>
                        )}
                    </Card>
                </TabPanel>
            </Tabs>

            <Snackbar
                open={snackbar.open}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
                autoHideDuration={4000}
                color={snackbar.color}
                variant="soft"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                {snackbar.message}
            </Snackbar>
        </Box>
    )
}
